import { authApi, getApiErrorMessage } from '@milliswift/api-client'
import { Button, Field, Input, PasswordStrength, useToast } from '@milliswift/ui'
import { zodResolver } from '@hookform/resolvers/zod'
import { KeyRound } from 'lucide-react'
import { useForm } from 'react-hook-form'
import { useSearchParams } from 'react-router-dom'
import { AuthCard, AuthPage } from '../components/AuthCard'
import { resetPasswordSchema, type ResetPasswordValues } from '../lib/schemas'

/**
 * The landing point of a reset link. The token is single-use; on success the
 * user is sent to sign-in with a banner, because they should authenticate with
 * the fresh password rather than being signed in implicitly.
 */
export default function ResetPassword() {
  const [params] = useSearchParams()
  const token = params.get('token') ?? ''
  const toast = useToast()

  const form = useForm<ResetPasswordValues>({
    resolver: zodResolver(resetPasswordSchema),
    defaultValues: { password: '', confirm_password: '' },
  })

  const password = form.watch('password')

  const submit = form.handleSubmit(async (values) => {
    try {
      await authApi.resetPassword({ token, new_password: values.password })
      window.location.href = '/login?reset=true'
    } catch (error) {
      toast.error('Could not reset your password', getApiErrorMessage(error))
    }
  })

  if (!token) {
    return (
      <AuthPage>
        <AuthCard title="Missing reset token">
          <p className="text-center text-sm text-muted">
            This page needs a reset link. Request a new one from{' '}
            <a href="/forgot-password" className="text-accent underline-offset-4 hover:underline">
              the reset page
            </a>
            .
          </p>
        </AuthCard>
      </AuthPage>
    )
  }

  return (
    <AuthPage>
      <AuthCard title="Choose a new password" subtitle="Single-use link — it expires after this.">
        <form onSubmit={submit} className="space-y-4" noValidate>
          <Field
            label="New password"
            required
            hint="At least 12 characters, with upper, lower, a number and a symbol."
            error={form.formState.errors.password?.message}
          >
            <Input
              type="password"
              autoComplete="new-password"
              placeholder="••••••••••••"
              leading={<KeyRound className="h-4 w-4" />}
              {...form.register('password')}
            />
          </Field>

          <Field
            label="Confirm password"
            required
            error={form.formState.errors.confirm_password?.message}
          >
            <Input
              type="password"
              autoComplete="new-password"
              placeholder="••••••••••••"
              leading={<KeyRound className="h-4 w-4" />}
              {...form.register('confirm_password')}
            />
          </Field>

          {password ? <PasswordStrength password={password} /> : null}

          <Button type="submit" className="w-full" loading={form.formState.isSubmitting}>
            Update password
          </Button>
        </form>
      </AuthCard>
    </AuthPage>
  )
}
